import { CartContext } from '@lib/cart/context'
import { getLocaleVariable } from '@lib/environment'
import { LanguageContext } from '@lib/language-context'
import { ShopContext } from '@lib/shop-context'
import { getShopifyDomain } from '@lib/shopify/client'
import { SiteContext } from '@lib/site-context'
import { useShopifyCookies } from '@shopify/hydrogen-react'
import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react'
import { defaultCookieConsentStatus, useCookieConsentStatus } from './consent'
import {
  getAnalyticsEvents,
  getReadyAnalyticsEvent,
  triggerGoogleTagManagerEvent,
  triggerShopifyEvent,
} from './events'
import { useTripleWhalePixel } from './triple-whale'
import {
  AnalyticsEvent,
  AnalyticsEventName,
  AnalyticsEventType,
  CookieConsentStatus,
  EventPayload,
  ShopifyEventPayload,
} from './types'
import { useWebgainsLandingPageTracking } from './webgains'

interface AnalyticsContextProps {
  cookieConsentStatus: CookieConsentStatus
  triggerAnalyticsEvent: (
    eventName: AnalyticsEventName,
    eventPayload?: EventPayload,
  ) => void
}

interface AnalyticsProviderProps {
  children: ReactNode
}

export const AnalyticsContext = createContext<AnalyticsContextProps>({
  cookieConsentStatus: defaultCookieConsentStatus,
  triggerAnalyticsEvent: () => null,
})

export const AnalyticsContextProvider = ({
  children,
}: AnalyticsProviderProps) => {
  const { cart } = useContext(CartContext)
  const { locale } = useContext(LanguageContext)
  const { shopId, currencyCode } = useContext(ShopContext)
  const { settings } = useContext(SiteContext)

  const [eventQueue, setEventQueue] = useState<AnalyticsEvent[]>([])

  // Process event queue when events are added or other parameters change
  useEffect(() => {
    const event = getReadyAnalyticsEvent(
      eventQueue,
      shopId,
      currencyCode,
      cart?.id,
    )

    if (!event) {
      return
    }

    // Remove event from queue
    setEventQueue((currentEventQueue) => {
      return currentEventQueue.filter((eventItem) => eventItem.id !== event.id)
    })

    if (event.type === AnalyticsEventType.GoogleTagManager) {
      triggerGoogleTagManagerEvent(event.name, event.eventPayload)
    }

    if (event.type === AnalyticsEventType.Shopify) {
      triggerShopifyEvent(
        event.name,
        event.eventPayload as ShopifyEventPayload,
        shopId,
        currencyCode,
        cart?.id,
        getShopifyDomain(locale),
      )
    }
  }, [cart, currencyCode, eventQueue, locale, setEventQueue, shopId])

  const triggerAnalyticsEvent = useCallback(
    (eventName: AnalyticsEventName, eventPayload?: EventPayload) => {
      const events = getAnalyticsEvents(eventName, eventPayload ?? {}, {
        googleEvents: !!settings?.googleEvents,
        shopifyEvents: !!settings?.shopifyEvents,
      })

      setEventQueue((currentEventQueue) => {
        return [...currentEventQueue, ...events]
      })
    },
    [setEventQueue, settings],
  )

  // Trigger pageview event on page load
  useEffect(() => {
    triggerAnalyticsEvent(AnalyticsEventName.Pageview)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const cookieConsentStatus = useCookieConsentStatus()

  // Shopify analytics
  useShopifyCookies({
    hasUserConsent: cookieConsentStatus.marketing,
    domain: getLocaleVariable(locale, 'NEXT_PUBLIC_SHOPIFY_COOKIE_DOMAIN'),
  })

  // Webgains tracking
  useWebgainsLandingPageTracking({
    hasUserConsent: cookieConsentStatus.marketing,
    programId: settings?.webgainsProgramId,
  })

  // Triple Whale pixel
  useTripleWhalePixel({
    disabled: !settings?.tripleWhaleEvents,
    hasUserConsent: cookieConsentStatus.marketing,
    tripleName: settings?.tripleWhalePixelId,
  })

  return (
    <AnalyticsContext.Provider
      value={{
        cookieConsentStatus,
        triggerAnalyticsEvent,
      }}
    >
      {children}
    </AnalyticsContext.Provider>
  )
}
